import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { fullName, getUserInitials } from '~/infra/utils';
import { BusinessPartyTypeEnum } from '~/store/business-types';
import { BusinessSettingsModel } from './BusinessSettingsModel';
export const VENDOR_FEATURE_PERMISSION_KEYS = [
    'BULK_CREATE_DRAFTS'
];
export function matchWorksAt(worksAt, visitor) {
    switch(worksAt.type){
        case BusinessPartyTypeEnum.Vendor:
            return visitor.vendor(worksAt);
        case BusinessPartyTypeEnum.Customer:
            return visitor.customer(worksAt);
        case BusinessPartyTypeEnum.MultiVendorCustomer:
            return visitor.multiVendorCustomer(worksAt);
    }
}
export var ColumnPaymentsStatus = /*#__PURE__*/ function(ColumnPaymentsStatus) {
    ColumnPaymentsStatus["Enabled"] = "enabled";
    ColumnPaymentsStatus["Disabled"] = "disabled";
    return ColumnPaymentsStatus;
}({});
export var UserRole = /*#__PURE__*/ function(UserRole) {
    UserRole["Member"] = "member";
    UserRole["Admin"] = "admin";
    UserRole["Owner"] = "owner";
    UserRole["AnchorAdmin"] = "anchorAdmin";
    UserRole["Reviewer"] = "reviewer";
    return UserRole;
}({});
const isAnyCustomer = ()=>true;
const isNotCustomer = ()=>false;
export class WorkspaceModel {
    static fromDTO(dto) {
        return new WorkspaceModel(dto);
    }
    match(visitor) {
        switch(this.data.type){
            case BusinessPartyTypeEnum.Vendor:
                return visitor.vendor(this.data);
            case BusinessPartyTypeEnum.Customer:
                return visitor.customer(this.data);
            case BusinessPartyTypeEnum.MultiVendorCustomer:
                return visitor.multiVendorCustomer(this.data);
        }
    }
    toDTO() {
        return this.data;
    }
    toIdAndName() {
        return {
            id: this.data.id,
            name: this.data.name
        };
    }
    id() {
        return this.data.id;
    }
    name() {
        return this.data.name;
    }
    setName(name) {
        return new WorkspaceModel(_object_spread_props(_object_spread({}, this.data), {
            name
        }));
    }
    visibility() {
        return this.data.visibility;
    }
    isHidden() {
        return this.data.visibility === 'hidden';
    }
    setVisibility(visibility) {
        return new WorkspaceModel(_object_spread_props(_object_spread({}, this.data), {
            visibility
        }));
    }
    logoUrl() {
        return this.data.logoUrl;
    }
    setupCompleted() {
        return this.data.setupCompleted;
    }
    isAnchorAdmin() {
        return this.data.role === "anchorAdmin";
    }
    isCustomer() {
        return this.match({
            vendor: isNotCustomer,
            customer: isAnyCustomer,
            multiVendorCustomer: isAnyCustomer
        });
    }
    isCustomerOfVendor(vendorId) {
        return this.data.type === BusinessPartyTypeEnum.Customer && this.data.customer.vendor.id === vendorId;
    }
    vendorName() {
        return this.data.type === BusinessPartyTypeEnum.Customer ? this.data.customer.vendor.name : undefined;
    }
    isVendor() {
        return this.data.type === BusinessPartyTypeEnum.Vendor;
    }
    isCompletelySetupAndDisplayable() {
        if (this.isAnchorAdmin()) {
            return false;
        }
        return this.isCompletelySetup();
    }
    isDisplayableInSwitcher() {
        if (this.isVendor()) {
            return !this.isAnchorAdmin();
        }
        return this.isCompletelySetupAndDisplayable();
    }
    isCompletelySetup() {
        return this.isCustomer() && [
            "owner",
            "admin",
            "member"
        ].includes(this.data.role) || this.isVendor() && this.setupCompleted();
    }
    getBusinessSwitcherItemSubtext(texts) {
        return this.match({
            vendor: ()=>texts.serviceProviderAccount(),
            customer: (workspace)=>texts.clientOf({
                    vendorName: workspace.customer.vendor.name
                }),
            multiVendorCustomer: ()=>texts.clientAccount()
        });
    }
    getBusinessSwitcherItemLogoUrl() {
        return this.match({
            vendor: (workspace)=>workspace.logoUrl || '',
            customer: (workspace)=>workspace.customer.vendor.logoUrl || '',
            multiVendorCustomer: ()=>''
        });
    }
    setLogoUrl(logoUrl) {
        return new WorkspaceModel(_object_spread_props(_object_spread({}, this.data), {
            logoUrl
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
// WHY: session payload sends 'n-a' as vendor name when there is no direct vendor link (business-type-mapper.go).
const UNRESOLVED_VENDOR_NAME = 'n-a';
const resolvedVendorName = (vendorName)=>(vendorName === null || vendorName === void 0 ? void 0 : vendorName.trim().toLowerCase()) === UNRESOLVED_VENDOR_NAME ? undefined : vendorName;
export class UserInfoModel {
    static fromDTO(dto) {
        return new UserInfoModel(dto);
    }
    id() {
        return this.data.id;
    }
    email() {
        return this.data.email;
    }
    firstName() {
        return this.data.firstName;
    }
    lastName() {
        return this.data.lastName;
    }
    fullName() {
        return fullName(this.data.firstName || null, this.data.lastName || null);
    }
    phone() {
        return this.data.phone;
    }
    personalSignature() {
        var _this_data_personalSignature;
        return (_this_data_personalSignature = this.data.personalSignature) !== null && _this_data_personalSignature !== void 0 ? _this_data_personalSignature : null;
    }
    hasPersonalSignature() {
        return this.data.personalSignature != null;
    }
    addSignatureByDefault() {
        var _ref;
        var _this_data_personalSignature;
        return (_ref = (_this_data_personalSignature = this.data.personalSignature) === null || _this_data_personalSignature === void 0 ? void 0 : _this_data_personalSignature.addByDefault) !== null && _ref !== void 0 ? _ref : false;
    }
    userInitials() {
        return getUserInitials({
            firstName: this.data.firstName,
            lastName: this.data.lastName,
            email: this.data.email
        });
    }
    referredByBusinessId() {
        var _this_data_referredBy;
        return (_this_data_referredBy = this.data.referredBy) === null || _this_data_referredBy === void 0 ? void 0 : _this_data_referredBy.businessId;
    }
    hasApprovedTermsOfService() {
        return this.data.hasApprovedTermsOfService;
    }
    numClientsAMonth() {
        return this.data.numClientsAMonth;
    }
    oAuthProvider() {
        return this.data.oAuthProvider;
    }
    identityTypes() {
        return this.data.identityTypes;
    }
    intercomUserJwt() {
        return this.data.intercomUserJwt;
    }
    hasIdentity() {
        var _this_data_identityTypes;
        return !!((_this_data_identityTypes = this.data.identityTypes) === null || _this_data_identityTypes === void 0 ? void 0 : _this_data_identityTypes.length);
    }
    loggedInViaLink() {
        return this.data.identityTypes === null;
    }
    hasPassword() {
        var _this_data_identityTypes;
        return !!((_this_data_identityTypes = this.data.identityTypes) === null || _this_data_identityTypes === void 0 ? void 0 : _this_data_identityTypes.includes('password'));
    }
    workspaces() {
        var _this_data_workspaces;
        return (_this_data_workspaces = this.data.workspaces) === null || _this_data_workspaces === void 0 ? void 0 : _this_data_workspaces.map(WorkspaceModel.fromDTO);
    }
    vendorNameById(vendorId) {
        var _this_workspaces_find, _this_workspaces;
        const worksAt = this.data.worksAt;
        if ((worksAt === null || worksAt === void 0 ? void 0 : worksAt.type) === BusinessPartyTypeEnum.Customer && worksAt.customer.vendor.id === vendorId) {
            return resolvedVendorName(worksAt.customer.vendor.name);
        }
        return resolvedVendorName((_this_workspaces = this.workspaces()) === null || _this_workspaces === void 0 ? void 0 : (_this_workspaces_find = _this_workspaces.find((workspace)=>workspace.isCustomerOfVendor(vendorId))) === null || _this_workspaces_find === void 0 ? void 0 : _this_workspaces_find.vendorName());
    }
    hasAVendorAccount() {
        return (this.workspaces() || []).some((workspace)=>workspace.isVendor());
    }
    business() {
        return this.data.worksAt;
    }
    hasBusiness() {
        return !!(this.data.worksAt && this.data.worksAt.name && (this.data.worksAt.businessTypes.length || this.data.worksAt.description));
    }
    businessId() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.id;
    }
    businessSettings() {
        return this._businessSettings;
    }
    businessName() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.name;
    }
    businessLogoUrl() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.logoUrl;
    }
    disabledPayments() {
        return this.data.worksAt ? this.data.worksAt.disablePayments : false;
    }
    /** Returns the role of the user in his current business. */ role() {
        var _this_data_worksAt;
        return (_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.role;
    }
    isVendor() {
        var _this_data_worksAt;
        return ((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.type) == BusinessPartyTypeEnum.Vendor;
    }
    isPermittedTo(key) {
        const worksAt = this.data.worksAt;
        if ((worksAt === null || worksAt === void 0 ? void 0 : worksAt.type) !== BusinessPartyTypeEnum.Vendor) {
            return false;
        }
        return worksAt.vendor.featurePermissions.some((permission)=>permission.key === key && permission.enabled);
    }
    isCustomer() {
        const worksAt = this.data.worksAt;
        return worksAt != null && matchWorksAt(worksAt, {
            vendor: isNotCustomer,
            customer: isAnyCustomer,
            multiVendorCustomer: isAnyCustomer
        });
    }
    isOwner() {
        return this.role() === "owner";
    }
    isAdmin() {
        return this.role() === "admin";
    }
    isReviewer() {
        return this.role() === "reviewer";
    }
    paymentsDisabled() {
        var _this_data_worksAt;
        return !!((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : _this_data_worksAt.disablePayments);
    }
    isExternalAccountErrored() {
        var _this_data_worksAt_stripe_externalAccountStatus, _this_data_worksAt_stripe, _this_data_worksAt;
        return ((_this_data_worksAt = this.data.worksAt) === null || _this_data_worksAt === void 0 ? void 0 : (_this_data_worksAt_stripe = _this_data_worksAt.stripe) === null || _this_data_worksAt_stripe === void 0 ? void 0 : (_this_data_worksAt_stripe_externalAccountStatus = _this_data_worksAt_stripe.externalAccountStatus) === null || _this_data_worksAt_stripe_externalAccountStatus === void 0 ? void 0 : _this_data_worksAt_stripe_externalAccountStatus.status) === 'errored';
    }
    columnEntityCreated() {
        return !!this.data.worksAt && this.data.worksAt.column.paymentsStatus === "enabled";
    }
    hasColumnAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.accountId;
    }
    hasColumnWalletAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.walletAccountId;
    }
    hasColumnPayoutAccountId() {
        return !!this.data.worksAt && !!this.data.worksAt.column.payoutAccountId;
    }
    setWorksAt(worksAt) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            worksAt
        }));
    }
    setNumClientsAMonth(numClientsAMonth) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            numClientsAMonth
        }));
    }
    updateUserDetails(data) {
        return new UserInfoModel(_object_spread({}, this.data, data));
    }
    setBranding(branding) {
        const worksAt = this.data.worksAt ? _object_spread_props(_object_spread({}, this.data.worksAt), {
            branding
        }) : undefined;
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            worksAt
        }));
    }
    setIntercomUserJwt(jwt) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            intercomUserJwt: jwt
        }));
    }
    setWorkspaces(workspaces) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            workspaces
        }));
    }
    setWorkspaceModels(workspaces) {
        return new UserInfoModel(_object_spread_props(_object_spread({}, this.data), {
            workspaces: workspaces === null || workspaces === void 0 ? void 0 : workspaces.map((workspace)=>workspace.toDTO())
        }));
    }
    constructor(data){
        var _data_worksAt;
        _define_property(this, "data", void 0);
        _define_property(this, "_businessSettings", void 0);
        this.data = data;
        this._businessSettings = ((_data_worksAt = data.worksAt) === null || _data_worksAt === void 0 ? void 0 : _data_worksAt.settings) && BusinessSettingsModel.fromDTO(data.worksAt.settings);
    }
}
